/**
 * Structural Summary — issue #819 Phase 1.
 *
 * Top-level component rendered at the top of DashboardPage. Fetches
 * the cross-project (or per-project) structural payload and renders
 * the six tiles, hotspots/orphans, health strip, graph structure
 * section, and the project matrix.
 *
 * No charting library dep. Stacked bars are plain flex divs with
 * percentage widths — good enough for a one-dimensional distribution
 * and keeps the web bundle lean.
 */

import { useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { InfoTooltip } from '../ui/InfoTooltip';
import type {
  StructuralSummaryPayload,
  DistributionSegment,
  HotspotRow,
  OrphanRow,
  CouplingRow,
  ExternalServiceRow,
  DbTableRow,
  ProjectMatrixRow,
} from '@veodiagram/shared';

type SortKey =
  | 'projectName'
  | 'lastAnalyzedAt'
  | 'userExperiences'
  | 'apiEndpoints'
  | 'unambiguousE2EFlows'
  | 'ambiguousStitches'
  | 'unresolvedStitches'
  | 'evidenceCoverage';

export function StructuralSummary() {
  const [data, setData] = useState<StructuralSummaryPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [focusProjectId, setFocusProjectId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    async (refresh = false) => {
      if (refresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const payload = await api.getStructuralSummary({
          projectId: focusProjectId ?? undefined,
          refresh,
        });
        setData(payload);
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load structural summary');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [focusProjectId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) {
    return (
      <section
        id="structural-summary"
        data-testid="structural-summary"
        tabIndex={-1}
        className="border border-border rounded-lg p-6 mb-6 text-sm text-muted-foreground scroll-mt-20"
      >
        Loading structural summary…
      </section>
    );
  }
  if (error) {
    return (
      <section
        id="structural-summary"
        data-testid="structural-summary"
        tabIndex={-1}
        className="border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 text-sm text-red-700 dark:text-red-300 scroll-mt-20"
      >
        {error}
      </section>
    );
  }
  if (!data) return null;

  return (
    <section
      id="structural-summary"
      data-testid="structural-summary"
      tabIndex={-1}
      className="mb-8 space-y-6 scroll-mt-20"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Structural summary
            <span
              data-testid="structural-source-chip"
              className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 uppercase tracking-wide"
              title="Derived from your analyzed code — this is live data"
            >
              live
            </span>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {focusProjectId
              ? `Filtered to one project · ${data.projectCount} project${data.projectCount === 1 ? '' : 's'}`
              : `Across ${data.projectCount} project${data.projectCount === 1 ? '' : 's'}`}
            {' · updated '}
            {new Date(data.generatedAt).toLocaleTimeString()}
          </p>
        </div>
        <div className="flex gap-2">
          {focusProjectId && (
            <button
              onClick={() => setFocusProjectId(null)}
              className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
            >
              Clear filter
            </button>
          )}
          <button
            onClick={() => void load(true)}
            disabled={refreshing}
            className="text-xs px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-50"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <ExecutiveTiles data={data} />

      {data.tiles.stitchesUnavailableForProjects > 0 && (
        <div
          data-testid="stitches-unavailable-banner"
          className="text-xs px-3 py-2 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 rounded"
        >
          Stitch metrics are unavailable for{' '}
          {data.tiles.stitchesUnavailableForProjects} project
          {data.tiles.stitchesUnavailableForProjects === 1 ? '' : 's'} (no local checkout).
          Confidence and ambiguous / unresolved counts only reflect locally analyzed projects.
        </div>
      )}

      <HealthStripBars strip={data.healthStrip} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HotspotsList hotspots={data.hotspots} />
        <OrphansList orphans={data.orphans} />
      </div>

      <CrossProjectMatrix
        rows={data.projectMatrix}
        onFocus={setFocusProjectId}
      />

      <GraphStructureSection
        topAfferent={data.graphStructure.topAfferent}
        topEfferent={data.graphStructure.topEfferent}
        externalServices={data.graphStructure.externalServices}
        dbTables={data.graphStructure.dbTables}
      />
    </section>
  );
}

// ─────────────────────────── executive tiles ───────────────────────────

function ExecutiveTiles({ data }: { data: StructuralSummaryPayload }) {
  const t = data.tiles;
  const tiles = [
    { testId: 'tile-user-experiences', label: 'User experiences', value: t.userExperiences, tone: 'blue' as const },
    { testId: 'tile-api-endpoints', label: 'API endpoints', value: t.apiEndpoints, tone: 'blue' as const },
    {
      testId: 'tile-unambiguous-flows',
      label: 'Unambiguous E2E flows',
      value: t.unambiguousE2EFlows,
      tone: 'green' as const,
    },
    {
      testId: 'tile-ambiguous',
      label: 'Ambiguous stitches',
      value: t.ambiguousStitches,
      tone: 'amber' as const,
    },
    {
      testId: 'tile-unresolved',
      label: 'Unresolved stitches',
      value: t.unresolvedStitches,
      tone: 'red' as const,
    },
    {
      testId: 'tile-evidence-coverage',
      label: 'Evidence coverage',
      value: `${Math.round(t.evidenceCoverage * 100)}%`,
      tone: 'slate' as const,
    },
  ];

  const toneMap = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
    slate: 'text-slate-600 dark:text-slate-300',
  };

  return (
    <div
      data-testid="executive-tiles"
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
    >
      {tiles.map(tile => (
        <div
          key={tile.label}
          data-testid={tile.testId}
          className="p-4 bg-muted/40 border border-border rounded"
        >
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {tile.label}
          </div>
          <div className={`text-2xl font-semibold mt-1 ${toneMap[tile.tone]}`}>
            {tile.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────── health strip ───────────────────────────

function HealthStripBars({
  strip,
}: {
  strip: StructuralSummaryPayload['healthStrip'];
}) {
  // #997: each bar gets a tooltip explaining what it measures, the
  // bucket criteria, and the data source. Copy verified against the
  // server-side aggregators in
  // `apps/api/src/services/dashboard/aggregators/healthStrip.ts`.
  //
  // #998: "Endpoint auth" and "Frameworks" don't carry useful
  // distribution information in a flat distribution bar — auth
  // typically collapses to a single segment (all routes share a
  // state), and frameworks were hard-coded to one blue tone so
  // segment widths were the only signal. Both now render as
  // bespoke summaries (status pill / colored chips) that convey
  // their data shape directly.
  const authTooltip: ReactNode = (
    <>
      <strong>Whether each backend route is guarded by an authentication check.</strong>
      <br />
      <em>Protected</em>: handler is wrapped by a known auth middleware
      (Fastify <code>preHandler: authenticate</code>, Express auth, Next
      middleware). <em>Unprotected</em>: no auth wrapper detected.
      <em>Unknown</em>: framework adapter doesn't yet support auth
      detection. From <code>BackendCallGraph.endpoints[].requiresAuth</code>.
    </>
  );
  const frameworksTooltip: ReactNode = (
    <>
      <strong>Distribution of backend route handlers across detected frameworks.</strong>
      <br />
      Fastify, Express, Hono, Next API routes, tRPC, Supabase Edge
      Functions, etc. Useful for spotting unintended framework mixing
      within one repo. From <code>BackendCallGraph.endpoints[].framework</code>.
    </>
  );
  const bars: Array<{ label: string; segments: DistributionSegment[]; tooltip: ReactNode }> = [
    {
      label: 'Stitch confidence',
      segments: strip.stitchConfidence,
      tooltip: (
        <>
          <strong>How confidently each frontend call site is bound to a backend route.</strong>
          <br />
          <em>Confident</em>: auto-stitched at confidence ≥ 0.9, plus user-
          confirmed and accepted-AI stitches. <em>Ambiguous</em>: multiple
          candidates scored similarly — awaiting human pick. <em>Unresolved</em>:
          no candidate matched. Source: <code>produceAutoStitches</code> in
          the analysis pipeline.
        </>
      ),
    },
    {
      label: 'Complexity',
      segments: strip.complexity,
      tooltip: (
        <>
          <strong>Per-function cyclomatic complexity.</strong>
          <br />
          Buckets: <code>1-5</code> (simple), <code>6-10</code> (moderate),
          <code>11-20</code> (complex), <code>20+</code> (very complex). AST
          walk on every function in the project's call graph; per-node{' '}
          <code>complexityCyclomatic</code> property.
        </>
      ),
    },
    {
      label: 'Dynamic endpoints',
      segments: strip.dynamicEndpoints,
      tooltip: (
        <>
          <strong>How often the static tracer could resolve a frontend call site's URL.</strong>
          <br />
          <em>Resolvable</em>: the URL was determinable as a literal pattern.
          <em>Dynamic</em>: <code>{'{dynamic}'}</code> /{' '}
          <code>{'{traced-via-call-graph}'}</code> / unresolved{' '}
          <code>{'${ENV_VAR}'}</code> — analysis couldn't follow deterministically.
          From <code>FrontendCallGraph.apiCallSites[].isDynamicEndpoint</code>.
        </>
      ),
    },
  ];
  return (
    <div data-testid="health-strip" className="space-y-3">
      {bars.map(bar => (
        <DistributionBar
          key={bar.label}
          label={bar.label}
          segments={bar.segments}
          tooltip={bar.tooltip}
        />
      ))}
      <EndpointAuthSummary segments={strip.endpointAuth} tooltip={authTooltip} />
      <FrameworkChips segments={strip.frameworks} tooltip={frameworksTooltip} />
    </div>
  );
}

/**
 * #998: Replace the "Endpoint auth" distribution bar (which usually
 * collapses to a single uniform segment) with a status pill showing
 * the dominant state plus a small breakdown of the rest.
 */
function EndpointAuthSummary({
  segments,
  tooltip,
}: {
  segments: DistributionSegment[];
  tooltip: ReactNode;
}) {
  const byLabel = new Map(segments.map(s => [s.label, s.value]));
  const protectedN = byLabel.get('Protected') ?? 0;
  const unprotected = byLabel.get('Unprotected') ?? 0;
  const unknown = byLabel.get('Unknown') ?? 0;
  const total = protectedN + unprotected + unknown;

  if (total === 0) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span>Endpoint auth</span>
        <InfoTooltip>{tooltip}</InfoTooltip>
        <span className="italic">— no data</span>
      </div>
    );
  }

  const pct = (n: number) => Math.round((n / total) * 100);
  // Dominant-state heuristic. If 100% one bucket → just that pill.
  // Otherwise: lead pill + small rider for the rest.
  let pill: { label: string; tone: 'green' | 'red' | 'amber' | 'slate' };
  if (protectedN === total) pill = { label: '100% protected', tone: 'green' };
  else if (unprotected === total) pill = { label: '0% protected — all unprotected', tone: 'red' };
  else if (unknown === total) pill = { label: 'auth not detected (framework not supported)', tone: 'slate' };
  else if (protectedN >= unprotected + unknown)
    pill = { label: `${pct(protectedN)}% protected`, tone: 'amber' };
  else pill = { label: `${pct(protectedN)}% protected — mostly unprotected`, tone: 'red' };

  const toneClass: Record<typeof pill.tone, string> = {
    green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    slate: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  };

  return (
    <div data-testid="endpoint-auth-summary">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <span>Endpoint auth</span>
        <InfoTooltip>{tooltip}</InfoTooltip>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className={`inline-flex items-center px-2 py-0.5 rounded border font-medium ${toneClass[pill.tone]}`}>
          {pill.label}
        </span>
        <span className="text-muted-foreground font-mono">
          {protectedN} protected · {unprotected} unprotected · {unknown} unknown
        </span>
      </div>
    </div>
  );
}

/**
 * #998: Replace the "Frameworks" distribution bar (hard-coded to one
 * blue tone for every segment) with per-framework colored chips. The
 * chip width is proportional to count so the distribution is still
 * visible, but each framework now has a distinct color the user can
 * scan at a glance.
 */
function FrameworkChips({
  segments,
  tooltip,
}: {
  segments: DistributionSegment[];
  tooltip: ReactNode;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span>Frameworks</span>
        <InfoTooltip>{tooltip}</InfoTooltip>
        <span className="italic">— no data</span>
      </div>
    );
  }

  // Per-framework color palette — same hues that show up elsewhere
  // in the codebase for the corresponding framework when possible.
  const tones = [
    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800',
    'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-800',
    'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800',
    'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
  ];

  // Sort descending by count so dominant framework leads.
  const sorted = [...segments].sort((a, b) => b.value - a.value);

  return (
    <div data-testid="framework-chips">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <span>Frameworks</span>
        <InfoTooltip>{tooltip}</InfoTooltip>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        {sorted.map((seg, i) => {
          const pct = Math.round((seg.value / total) * 100);
          return (
            <span
              key={seg.label}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border font-medium ${tones[i % tones.length]}`}
              title={`${seg.label}: ${seg.value} endpoints (${pct}%)`}
            >
              {seg.label}
              <span className="font-mono opacity-80">{seg.value}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function DistributionBar({
  label,
  segments,
  tooltip,
}: {
  label: string;
  segments: DistributionSegment[];
  tooltip?: ReactNode;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) {
    return (
      <div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
          <span>{label}</span>
          {tooltip && <InfoTooltip>{tooltip}</InfoTooltip>}
          <span className="italic">— no data</span>
        </div>
        <div className="h-5 rounded bg-muted/40 border border-border" />
      </div>
    );
  }
  const toneMap: Record<string, string> = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    slate: 'bg-slate-400',
  };
  // Palette cycle for segments with no tone hint (e.g. frameworks).
  const cyclePalette = ['bg-blue-500', 'bg-indigo-500', 'bg-sky-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500'];
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
        <span className="flex items-center gap-1.5">
          <span>{label}</span>
          {tooltip && <InfoTooltip>{tooltip}</InfoTooltip>}
        </span>
        <span className="font-mono">
          {segments
            .map(s => `${s.label}: ${s.value}`)
            .join(' · ')}
        </span>
      </div>
      <div className="h-5 rounded overflow-hidden border border-border flex">
        {segments.map((seg, i) => {
          const width = (seg.value / total) * 100;
          const bg =
            (seg.tone && toneMap[seg.tone]) ??
            cyclePalette[i % cyclePalette.length];
          return (
            <div
              key={`${seg.label}-${i}`}
              className={bg}
              style={{ width: `${width}%` }}
              title={`${seg.label}: ${seg.value}`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────── hotspots / orphans ───────────────────────────

function HotspotsList({ hotspots }: { hotspots: HotspotRow[] }) {
  return (
    <div data-testid="hotspots-list" className="border border-border rounded">
      <div className="px-3 py-2 border-b border-border bg-muted/30 text-xs font-semibold uppercase text-muted-foreground">
        Most-used code — top {hotspots.length}
      </div>
      {hotspots.length === 0 ? (
        <div className="p-3 text-xs text-muted-foreground">
          No hotspots detected yet.
        </div>
      ) : (
        <ul className="divide-y divide-border max-h-80 overflow-auto">
          {hotspots.map(h => (
            <li key={`${h.projectId}-${h.functionId}`} className="p-2 text-xs">
              <div className="font-mono truncate" title={h.functionName}>
                <span
                  className={`inline-block px-1 rounded text-[10px] mr-1 ${
                    h.side === 'frontend'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : h.side === 'backend'
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  }`}
                >
                  {h.side}
                </span>
                {h.functionName}
              </div>
              <div className="text-muted-foreground mt-0.5 truncate">
                {h.callerCount} callers · {h.projectName}
                {h.filePath && (
                  <>
                    {' · '}
                    <span className="font-mono">
                      {shortenPath(h.filePath)}
                      {h.lineStart ? `:${h.lineStart}` : ''}
                    </span>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrphansList({ orphans }: { orphans: OrphanRow[] }) {
  return (
    <div data-testid="orphans-list" className="border border-border rounded">
      <div className="px-3 py-2 border-b border-border bg-muted/30 text-xs font-semibold uppercase text-muted-foreground">
        Unused code / orphans — top {orphans.length}
      </div>
      {orphans.length === 0 ? (
        <div className="p-3 text-xs text-muted-foreground">
          No orphaned functions detected.
        </div>
      ) : (
        <ul className="divide-y divide-border max-h-80 overflow-auto">
          {orphans.map(o => (
            <li key={`${o.projectId}-${o.functionId}`} className="p-2 text-xs">
              <div className="font-mono truncate" title={o.functionName}>
                {o.functionName}
              </div>
              <div className="text-muted-foreground mt-0.5 truncate">
                {o.projectName}
                {o.filePath && (
                  <>
                    {' · '}
                    <span className="font-mono">
                      {shortenPath(o.filePath)}
                      {o.lineStart ? `:${o.lineStart}` : ''}
                    </span>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────── cross-project matrix ───────────────────────────

function CrossProjectMatrix({
  rows,
  onFocus,
}: {
  rows: ProjectMatrixRow[];
  onFocus: (projectId: string) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('userExperiences');
  const [desc, setDesc] = useState(true);

  const sorted = [...rows].sort((a, b) => {
    const av = (a as unknown as Record<SortKey, unknown>)[sortKey];
    const bv = (b as unknown as Record<SortKey, unknown>)[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      return desc ? bv - av : av - bv;
    }
    const as = String(av);
    const bs = String(bv);
    return desc ? bs.localeCompare(as) : as.localeCompare(bs);
  });

  const toggle = (k: SortKey) => {
    if (sortKey === k) setDesc(d => !d);
    else {
      setSortKey(k);
      setDesc(true);
    }
  };

  return (
    <div
      data-testid="cross-project-matrix"
      className="border border-border rounded overflow-x-auto"
    >
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr className="text-left">
            <Th k="projectName" label="Project" sortKey={sortKey} desc={desc} onClick={toggle} />
            <Th k="userExperiences" label="UX" sortKey={sortKey} desc={desc} onClick={toggle} align="right" />
            <Th k="apiEndpoints" label="Endpoints" sortKey={sortKey} desc={desc} onClick={toggle} align="right" />
            <Th k="unambiguousE2EFlows" label="Clean flows" sortKey={sortKey} desc={desc} onClick={toggle} align="right" />
            <Th k="ambiguousStitches" label="Ambiguous" sortKey={sortKey} desc={desc} onClick={toggle} align="right" />
            <Th k="unresolvedStitches" label="Unresolved" sortKey={sortKey} desc={desc} onClick={toggle} align="right" />
            <Th k="evidenceCoverage" label="Evidence" sortKey={sortKey} desc={desc} onClick={toggle} align="right" />
            <Th k="lastAnalyzedAt" label="Last analyzed" sortKey={sortKey} desc={desc} onClick={toggle} />
            <th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {sorted.map(row => (
            <tr
              key={row.projectId}
              data-testid="matrix-row"
              data-project-id={row.projectId}
              className="border-t border-border hover:bg-muted/20"
            >
              <td className="p-2 font-medium">
                <Link
                  to={`/projects/${row.projectId}`}
                  className="hover:text-primary"
                >
                  {row.projectName}
                </Link>
                {!row.stitchesAvailable && (
                  <span
                    className="ml-1 text-[10px] px-1 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                    title="No local checkout — stitch metrics unavailable"
                  >
                    remote
                  </span>
                )}
              </td>
              <td className="p-2 text-right font-mono">{row.userExperiences}</td>
              <td className="p-2 text-right font-mono">{row.apiEndpoints}</td>
              <td className="p-2 text-right font-mono">{row.unambiguousE2EFlows ?? '—'}</td>
              <td className="p-2 text-right font-mono">{row.ambiguousStitches ?? '—'}</td>
              <td className="p-2 text-right font-mono">{row.unresolvedStitches ?? '—'}</td>
              <td className="p-2 text-right font-mono">
                {Math.round(row.evidenceCoverage * 100)}%
              </td>
              <td className="p-2 text-muted-foreground">
                {row.lastAnalyzedAt
                  ? new Date(row.lastAnalyzedAt).toLocaleDateString()
                  : '—'}
              </td>
              <td className="p-2 text-right">
                <button
                  onClick={() => onFocus(row.projectId)}
                  className="text-[11px] text-primary hover:underline"
                  data-testid="matrix-focus-button"
                >
                  Focus
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  k,
  label,
  sortKey,
  desc,
  onClick,
  align,
}: {
  k: SortKey;
  label: string;
  sortKey: SortKey;
  desc: boolean;
  onClick: (k: SortKey) => void;
  align?: 'right';
}) {
  const active = sortKey === k;
  return (
    <th
      className={`p-2 cursor-pointer select-none ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onClick(k)}
    >
      <span className={active ? 'text-foreground' : 'text-muted-foreground'}>
        {label}
        {active ? (desc ? ' ↓' : ' ↑') : ''}
      </span>
    </th>
  );
}

// ─────────────────────────── graph structure ───────────────────────────

function GraphStructureSection({
  topAfferent,
  topEfferent,
  externalServices,
  dbTables,
}: {
  topAfferent: CouplingRow[];
  topEfferent: CouplingRow[];
  externalServices: ExternalServiceRow[];
  dbTables: DbTableRow[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <details
      data-testid="graph-structure-section"
      open={open}
      onToggle={e => setOpen((e.target as HTMLDetailsElement).open)}
      className="border border-border rounded"
    >
      <summary className="px-3 py-2 bg-muted/30 text-xs font-semibold uppercase text-muted-foreground cursor-pointer">
        Graph structure metrics
      </summary>
      <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <CouplingTable title="Most depended-on modules (afferent)" rows={topAfferent} field="afferent" />
        <CouplingTable title="Most dependent modules (efferent)" rows={topEfferent} field="efferent" />
        <ExternalServiceTable rows={externalServices} />
        <DbTableTable rows={dbTables} />
      </div>
    </details>
  );
}

function CouplingTable({
  title,
  rows,
  field,
}: {
  title: string;
  rows: CouplingRow[];
  field: 'afferent' | 'efferent';
}) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">no data</div>
      ) : (
        <ul className="text-xs space-y-1">
          {rows.map(r => (
            <li
              key={`${r.projectId}-${r.module}`}
              className="flex items-center justify-between gap-2 font-mono"
            >
              <span className="truncate" title={r.module}>
                {shortenPath(r.module)}
              </span>
              <span className="text-muted-foreground">
                {r[field]} · {r.projectName}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ExternalServiceTable({ rows }: { rows: ExternalServiceRow[] }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">
        External service surface
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">no data</div>
      ) : (
        <ul className="text-xs space-y-1">
          {rows.map(r => (
            <li key={r.host} className="flex items-center justify-between gap-2 font-mono">
              <span className="truncate">{r.host}</span>
              <span className="text-muted-foreground">
                {r.callCount} calls · {r.projects} proj
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DbTableTable({ rows }: { rows: DbTableRow[] }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">
        Database tables touched
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">no data</div>
      ) : (
        <ul className="text-xs space-y-1 max-h-60 overflow-auto">
          {rows.map(r => (
            <li key={r.table} className="flex items-center justify-between gap-2 font-mono">
              <span>{r.table}</span>
              <span className="text-muted-foreground">
                {r.queries} queries · {r.projects} proj
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────── helpers ───────────────────────────

function shortenPath(p: string): string {
  const parts = p.split('/');
  return parts.slice(-3).join('/');
}
